import lodash from 'lodash'
import { app, BrowserWindow, BrowserView, WebContents, BrowserWindowConstructorOptions, session, Session, webContents, BrowserViewConstructorOptions } from 'electron'
import * as remote from '@electron/remote/main'
import eIpc from '@lynker-desktop/electron-ipc/main'
import md5 from 'md5'
import type { ElectronWindowsManagerOptions, BWItem, BVItem, PreloadWebContentsConfig } from '../common';

export const getCustomSession = (() => {
  let customSession: Session;
  return () => {
    if (!customSession) {
      customSession = session.fromPartition('persist:__global_main_session__');
    }
    return customSession;
  }
})();

app.on('ready', () => {
  getCustomSession();
});

if (!remote.isInitialized()) {
  remote.initialize();
}

export const enable = (win: WebContents) => {
  remote.enable(win)
}

const initWebContentsVal = (win: BWItem | BVItem, preload: string) => {
  win.webContents.executeJavaScript(`
    try {
      const data = {
        __ELECTRON_WINDOW_MANAGER_WEB_CONTENTS_ID__: ${JSON.stringify(win.webContents.id)},
        __ELECTRON_WINDOW_MANAGER_TYPE__: ${JSON.stringify(win._type)},
        __ELECTRON_WINDOW_MANAGER_NAME__: ${JSON.stringify(win._name)},
        __ELECTRON_WINDOW_MANAGER_EXTRA_DATA__: ${JSON.stringify(win._extraData || '')},
        __ELECTRON_WINDOW_MANAGER_PRELOAD__: ${JSON.stringify(preload)},
        __ELECTRON_WINDOW_MANAGER_INIT_URL__: ${JSON.stringify(win._initUrl || '')}
      };
      Object.entries(data).forEach(([key, value]) => {
        window[key] = value;
      });
    } catch (error) {}
  `)
}

export class WindowsManager {
  loadingViewUrl: string | undefined;
  errorViewUrl: string | undefined;
  preload?: string;
  // 预加载的webContents配置
  preloadWebContentsConfig?: PreloadWebContentsConfig;
  windows: Map<number, BWItem | BVItem>;
  // 预加载的窗口
  preloadedBW: BWItem | null = null;
  // 预加载的窗口（无边框，有按钮）
  preloadedBW_FramelessWithButtons: BWItem | null = null;
  // 预加载的窗口（无边框，无按钮）
  preloadedBW_FramelessNoButtons: BWItem | null = null;
  // 预加载的浏览器视图
  preloadedBV: BVItem | null = null;
  preloading: boolean = false;
  webviewDomainWhiteList: string[] = [];
  // 创建队列相关属性
  private createQueue: Array<{
    options: ElectronWindowsManagerOptions;
    resolve: (window: BVItem | BWItem) => void;
    reject: (error: any) => void;
  }> = [];
  private isCreating: boolean = false;
  /**
   * webview 域名白名单
   * 传入格式示例：
   *   [
   *     'example.com',         // 精确匹配 example.com
   *     '.example.com',        // 匹配所有 example.com 的子域名，如 a.example.com、b.example.com
   *     'sub.domain.com',      // 精确匹配 sub.domain.com
   *     'localhost',           // 匹配 localhost 及本地回环地址
   *     '127.0.0.1',           // 匹配 127.0.0.1
   *     '::1'                  // 匹配 IPv6 本地回环
   *   ]
   * 注意：
   *   - 以点开头的（如 .example.com）会匹配所有子域名。
   *   - 不带点的（如 example.com）只匹配主域名。
   *   - 'localhost'、'127.0.0.1'、'::1' 以及局域网 IP（如 192.168.x.x、10.x.x.x、172.16.x.x~172.31.x.x）都视为本地白名单。
   */
  constructor(preload?: string, loadingViewUrl?: string, errorViewUrl?: string, preloadWebContentsConfig?: PreloadWebContentsConfig, webviewDomainWhiteList?: string[]) {
    this.preload = preload;
    this.windows = new Map();
    this.loadingViewUrl = `${loadingViewUrl ?? ''}`;
    this.errorViewUrl = `${errorViewUrl ?? ''}`;
    this.preloadWebContentsConfig = preloadWebContentsConfig;
    this.webviewDomainWhiteList = webviewDomainWhiteList || [];
    log('log', 'preloadWebContentsConfig: ', this.preloadWebContentsConfig);
    if (this.preloadWebContentsConfig) {
      app.whenReady().then(() => {
        if (this.preloadWebContentsConfig) {
          this.setPreloadWebContentsConfig(this.preloadWebContentsConfig);
        }
      });
    }
  }

  /**
   * 设置预加载的webContents配置
   * @param preloadWebContentsConfig 预加载的webContents配置
   */
  setPreloadWebContentsConfig(preloadWebContentsConfig: PreloadWebContentsConfig) {
    try {
      this.preloadWebContentsConfig = preloadWebContentsConfig;
      if (this.preloadWebContentsConfig) {
        this._preloadInstances();
      } else {
        this.preloadedBW = null;
        this.preloadedBW_FramelessWithButtons = null;
        this.preloadedBW_FramelessNoButtons = null;
        this.preloadedBV = null;
      }
    } catch (error) {
      log('error', 'setPreloadWebContentsConfig error:', error);
    }
  }

  /**
   * 预加载实例
   */
  private async _preloadInstances() {
    if (this.preloading) return;
    this.preloading = true;
    try {
      if (this.preloadWebContentsConfig) {
        log('log', 'preloadWebContentsConfig: ', this.preloadWebContentsConfig);
        // 根据配置决定是否预加载普通窗口
        if (this.preloadWebContentsConfig.enableBW !== false) {
          if (!this.preloadedBW) {
            this._createPreloadBW({}).then(i => {
              this.preloadedBW = i;
              log('log', 'init preloadedBW: ', !!this.preloadedBW);
            });
          }
        }
        // 根据配置决定是否预加载无边框有按钮的窗口
        if (this.preloadWebContentsConfig.enableBW_FramelessWithButtons !== false) {
          if (!this.preloadedBW_FramelessWithButtons) {
            this._createPreloadBW({
              frame: false,
              transparent: true,
              autoHideMenuBar: true,
              titleBarStyle: 'hidden',
            }).then(i => {
              this.preloadedBW_FramelessWithButtons = i;
              log('log', 'init preloadedBW_FramelessWithButtons: ', !!this.preloadedBW_FramelessWithButtons);
            });
          }
        }
        // 根据配置决定是否预加载无边框无按钮的窗口
        if (this.preloadWebContentsConfig.enableBW_FramelessNoButtons !== false) {
          if (!this.preloadedBW_FramelessNoButtons) {
            this._createPreloadBW({
              frame: false,
              transparent: true,
              autoHideMenuBar: true,
              titleBarStyle: 'default',
            }).then(i => {
              this.preloadedBW_FramelessNoButtons = i;
              log('log', 'init preloadedBW_FramelessNoButtons: ', !!this.preloadedBW_FramelessNoButtons);
            });
          }
        }
        // 根据配置决定是否预加载浏览器视图
        if (this.preloadWebContentsConfig.enableBV !== false) {
          if (!this.preloadedBV) {
            this._createPreloadBV().then(i => {
              this.preloadedBV = i;
              log('log', 'init preloadedBV: ', !!this.preloadedBV);
            });
          }
        }
      }
    } catch (e) {
      log('error', '预创建实例失败', e);
    }
    this.preloading = false;
  }

  /**
   * 创建预加载的窗口
   * @param options 窗口选项
   * @returns 预加载的窗口
   */
  private _createPreloadBW(options: Partial<BrowserWindowConstructorOptions> = {}): Promise<BWItem> {
    return new Promise((resolve) => {
      const preload = this.preload;
      const url = this.preloadWebContentsConfig?.url;
      if (this.preloadWebContentsConfig?.url) {
        const webPreferences = (options.webPreferences || {});
        const instance = new BrowserWindow({
          show: false,
          backgroundColor: '#ffffff',
          ...options,
          webPreferences: {
            ...webPreferences,
            webviewTag: true,
            plugins: true,
            nodeIntegration: true,
            contextIsolation: false,
            backgroundThrottling: false,
            webSecurity: false,
            preload: webPreferences.preload || preload,
          }
        });
        try {
          remote.enable(instance.webContents);
        } catch (error) {
          log('error', '预加载 BW 设置 remote 失败', error);
        }
        try {
          // @ts-ignore
          instance._id = instance.webContents.id;
        } catch (error) {
          log('error', '预加载 BW 设置 _id 失败', error);
        }
        // @ts-ignore
        log('log', '创建预BW: ', instance._id, this.preloadWebContentsConfig?.url);
        // instance.webContents.once('did-finish-load', () => {
        //   resolve(instance as BWItem);
        // });
        // instance.webContents.once('did-fail-load', () => {
        //   resolve(instance as BWItem);
        // });
        instance.loadURL(url ? `${url}` : 'about:blank');
        resolve(instance as BWItem);
      } else {
        resolve(null as any);
      }
    });
  }

  /**
   * 创建预加载的浏览器视图
   * @returns 预加载的浏览器视图
   */
  private _createPreloadBV(options: Partial<BrowserViewConstructorOptions> = {}): Promise<BVItem> {
    return new Promise((resolve) => {
      const preload = this.preload;
      const url = this.preloadWebContentsConfig?.url;
      if (this.preloadWebContentsConfig?.url) {
        const webPreferences = (options.webPreferences || {});
        const instance = new BrowserView({
          webPreferences: {
            ...webPreferences,
            webviewTag: true,
            plugins: true,
            nodeIntegration: true,
            contextIsolation: false,
            // backgroundThrottling: false,
            webSecurity: false,
            preload: webPreferences.preload || preload,
          }
        });
        try {
          remote.enable(instance.webContents);
        } catch (error) {
          log('error', '预加载 BV 设置 remote 失败', error);
        }
        try {
            // @ts-ignore
          instance._id = instance.webContents.id;
        } catch (error) {
          log('error', '预加载 BV 设置 _id 失败', error);
        }
        // @ts-ignore
        log('log', '创建预BV: ', instance._id, this.preloadWebContentsConfig?.url);
        // instance.webContents.once('did-finish-load', () => {
        //   resolve(instance as BVItem);
        // });
        // instance.webContents.once('did-fail-load', () => {
        //   resolve(instance as BVItem);
        // });
        instance.webContents.loadURL(url || 'about:blank');
        resolve(instance as BVItem);
      } else {
        resolve(null as any);
      }
    });
  }

  // 方法重载声明
  create(options: Omit<ElectronWindowsManagerOptions, 'type'> & { type?: 'BW' }): Promise<BWItem>;
  create(options: ElectronWindowsManagerOptions & { type: 'BV' }): Promise<BVItem>;
  create(options: ElectronWindowsManagerOptions): Promise<BVItem | BWItem>;

  create(options: ElectronWindowsManagerOptions): Promise<BVItem | BWItem> {
    return new Promise((resolve, reject) => {
      // 将创建请求添加到队列
      this.createQueue.push({ options, resolve, reject });

      // 如果当前没有在创建，则开始处理队列
      if (!this.isCreating) {
        this.processCreateQueue();
      }
    });
  }

  /**
   * 处理创建队列
   */
  private async processCreateQueue() {
    if (this.isCreating || this.createQueue.length === 0) {
      return;
    }

    this.isCreating = true;

    while (this.createQueue.length > 0) {
      const { options, resolve, reject } = this.createQueue.shift()!;

      try {
        const window = await this._createWindow(options);
        resolve(window);
      } catch (error) {
        log('error', 'create window failed:', error);
        reject(error);
      }
    }

    this.isCreating = false;
  }

  /**
   * 实际的窗口创建逻辑
   */
  private async _createWindow(options: ElectronWindowsManagerOptions): Promise<BVItem | BWItem> {
    let window: any;
    const {
      usePreload = true,
      type = 'BW',
      name = 'anonymous',
      url,
      loadingView = {url: undefined},
      errorView = {url: undefined},
      browserWindow: browserWindowOptions,
      openDevTools = false,
      preventOriginClose = false,
    } = options;
    options.type = type;
    // 优先复用预创建实例
    let preloadWin: BWItem | BVItem | null = null;
    if (type === 'BW' && usePreload && this.preloadWebContentsConfig?.url) {
      const bwOptions = (browserWindowOptions as import('../common').BrowserWindowType) || {};
      if (bwOptions.frame === false) {
        if (bwOptions.titleBarStyle === 'default' || !bwOptions.titleBarStyle) {
          if (this.preloadWebContentsConfig.enableBW_FramelessNoButtons !== false && this.preloadedBW_FramelessNoButtons) {
            preloadWin = this.preloadedBW_FramelessNoButtons;
            this.preloadedBW_FramelessNoButtons = await this._createPreloadBW({
              frame: false,
              transparent: true,
              titleBarStyle: 'default',
              webPreferences: {
                preload: bwOptions?.webPreferences?.preload || this.preload,
              }
            })
          }
        } else {
          if (this.preloadWebContentsConfig.enableBW_FramelessWithButtons !== false && this.preloadedBW_FramelessWithButtons) {
            preloadWin = this.preloadedBW_FramelessWithButtons;
            this.preloadedBW_FramelessWithButtons = await this._createPreloadBW({
              frame: false,
              transparent: true,
              titleBarStyle: 'hidden',
              webPreferences: {
                preload: this.preload,
              }
            })
          }
        }
      } else {
        if (this.preloadWebContentsConfig.enableBW !== false && this.preloadedBW) {
          preloadWin = this.preloadedBW;
          this.preloadedBW = await this._createPreloadBW({
            webPreferences: {
              preload: bwOptions?.webPreferences?.preload || this.preload,
            }
          })
        }
      }
    }
    if (type === 'BV' && usePreload && this.preloadWebContentsConfig?.url) {
      const bvOptions = (browserWindowOptions as import('../common').BrowserViewType) || {};
      if (this.preloadWebContentsConfig.enableBV !== false && this.preloadedBV) {
        preloadWin = this.preloadedBV;
        this.preloadedBV = await this._createPreloadBV({
          webPreferences: {
            preload: bvOptions?.webPreferences?.preload || this.preload,
          }
        })
      }
    }
    if (preloadWin) {
      const win = preloadWin;
      log('log', `${name} 使用预加载窗口(${type})`, win._id);
      win._type = 'BW';
      win._name = options.name || 'anonymous';
      win._extraData = `${options?.extraData || ''}`;
      win._initUrl = `${options?.url || ''}`;
      // @ts-ignore
      // win?.removeAllListeners && win?.removeAllListeners?.();
      // win.webContents.removeAllListeners && win.webContents.removeAllListeners();
      if (type === 'BW') {
        // @ts-ignore
        this._applyBrowserWindowOptions(win as BWItem, options);
      }
      if (type === 'BV') {
        this._applyBrowserViewOptions(win as BVItem, options);
      }
      if (typeof this.preloadWebContentsConfig?.customLoadURL === 'function') {
        try {
          if (type === 'BW') {
          // @ts-ignore
            const originLoadURL = win.loadURL;
          // @ts-ignore
            win.loadURL = async (url: string) => {
              return new Promise(async (resolve, reject) => {
                try {
                  console.error('customLoadURL win.loadURL');
                  // @ts-ignore
                  await this.preloadWebContentsConfig.customLoadURL(url || 'about:blank', (url) => originLoadURL.call(win, url), win.webContents);
                  try {
                    (win as BWItem).emit('ready-to-show');
                  } catch (error) {
                    log('error', 'emit ready-to-show event failed:', error);
                  }
                  resolve(undefined)
                } catch (error) {
                  reject(error);
                }
              })

            }
          }
          const originWebContentsLoadURL = win.webContents.loadURL;
          // @ts-ignore
          win.webContents.loadURL = async (url: string) => {
            return new Promise(async (resolve, reject) => {
              try {
                console.error('customLoadURL win.webContents.loadURL');
                // @ts-ignore
                await this.preloadWebContentsConfig.customLoadURL(url || 'about:blank', (url) => originWebContentsLoadURL.call(win.webContents, url), win.webContents);
                try {
                  (win as BVItem).webContents.emit('ready-to-show');
                } catch (error) {
                  log('error', 'emit ready-to-show event failed:', error);
                }
                resolve(undefined);
              } catch (error) {
                reject(error);
              }
            })
          }
        } catch (error) {
          console.error('customLoadURL error', error);
        }
      }
      window = win;
    }
    try {
      loadingView.url = `${loadingView?.url ?? this.loadingViewUrl}`;
      lodash.merge(
        options,
        {
          loadingView,
        }
      )
    } catch (error) {
      log('error', 'loadingView error:', loadingView, this.loadingViewUrl);
    }
    try {
      errorView.url = `${errorView?.url ?? this.errorViewUrl}`;
      lodash.merge(
        options,
        {
          errorView,
        }
      )
    } catch (error) {
      log('error', 'errorView error:', errorView, this.errorViewUrl);
    }
    try {
      let parentWin:BrowserWindowConstructorOptions['parent'] = undefined;
      if (typeof browserWindowOptions?.parent === 'number') {
        parentWin = BrowserWindow.fromId(browserWindowOptions?.parent) || undefined;
        if (parentWin) {
          browserWindowOptions.parent = parentWin;
        } else {
          browserWindowOptions.parent = undefined;
        }
      }
      log('log', 'create 1: ', options);
      log('log', 'create 2: ', `parentWin: ${parentWin?.id}`);
      const preload = browserWindowOptions?.webPreferences?.preload || this.preload;
      if (!window) {
        window = type === 'BV' ?
        new BrowserView(
          lodash.merge(
            (browserWindowOptions || {}),
            {
              webPreferences: lodash.merge({
                webviewTag: true,
                // session: getCustomSession(),
                plugins: true,
                nodeIntegration: true,
                contextIsolation: false,
                // fix bv二次挂载会灰屏
                // backgroundThrottling: false,
                nativeWindowOpen: true,
                webSecurity: false,
                preload: preload,
              }, browserWindowOptions?.webPreferences || {})
            },
          )
        ) as BVItem
      : new BrowserWindow(
          lodash.merge(
            {
              acceptFirstMouse: true,
            },
            (browserWindowOptions || {}),
            {
              parent: parentWin,
              webPreferences: lodash.merge({
                webviewTag: true,
                // session: getCustomSession(),
                plugins: true,
                nodeIntegration: true,
                contextIsolation: false,
                backgroundThrottling: false,
                nativeWindowOpen: true,
                webSecurity: false,
                preload: preload,
              }, browserWindowOptions?.webPreferences || {})
            },
          )
        ) as BWItem;
        log('log', `${name} 不使用 ${type === 'BV' ? 'preloadedBV' : 'preloadedBW'}`, window?.webContents?.id);
        try {
          remote.enable(window.webContents);
        } catch (error) {
          log('error', 'enable: ', error)
        }
      }
      // 停止加载
      // window.webContents?.stop?.();
      // @ts-ignore
      try {
        window.id = Number(`${window.id || window.webContents.id}`);
      } catch (error) {
        // log('error', 'set id: ', error)
      }
      // @ts-ignore
      try {
        window._id = Number(`${window.id || window.webContents.id}`);
      } catch (error) {
        // log('error', 'set id: ', error)
      }

      window._type = type;
      window._name = name;
      window._extraData = `${options?.extraData || ''}`;
      window._initUrl = `${options?.url || ''}`;
      log('log', 'create 5: ', window.id, window._id, window._name);
      if (loadingView?.url) {
        if (type === 'BW') {
          // @ts-ignore
          this._setLoadingView(window, options);
        }
      }
      if (errorView?.url) {
        if (type === 'BW') {
          const showErrorView = lodash.debounce(() => {
            const _url = window._initUrl;
            /**
             * 判断是否是错误视图
             */
            const isErrorView = `${_url}`.toUpperCase().startsWith(`${errorView?.url}`.toUpperCase());
            if (!isErrorView && _url) {
              // @ts-ignore
              window.loadURL ? window.loadURL(`${errorView?.url}`) : window.webContents.loadURL(`${errorView?.url}`);
              window.webContents.executeJavaScript(`
                var key = '__ELECTRON_WINDOW_MANAGER_DID_FAIL_LOAD_URL__';
                window[key] = "${_url}";
                sessionStorage.setItem(key, "${_url}");
              `);
            }
          }, 1000);
          // @ts-ignore
          window.webContents.on('did-fail-load', (event, errorCode, errorDescription, validatedURL, isMainFrame) => {
            if (isMainFrame) {
              showErrorView();
            }
          });
          // 当开始加载时取消错误视图
          window.webContents.on('did-start-loading', () => {
            showErrorView.cancel();
          });
          // 当导航开始时取消错误视图
          window.webContents.on('did-start-navigation', () => {
            showErrorView.cancel();
          });
          // 当页面重新加载时取消错误视图
          window.webContents.on('did-navigate', () => {
            showErrorView.cancel();
          });
          // 当页面完成加载时取消错误视图
          window.webContents.on('did-finish-load', () => {
            showErrorView.cancel();
          });
          // 当窗口关闭时取消错误视图
          window.webContents.on('close', () => {
            showErrorView.cancel();
          });
          // 当窗口销毁时取消错误视图
          window.webContents.on('destroyed', () => {
            showErrorView.cancel();
          });
        }
      }
      window.webContents.on('did-attach-webview', (_event: any, webContents: any) => {
        const tryEnable = () => {
          const url = webContents.getURL();
          // 判断是否本地/内网IP
          const isLocalhost = (hostname: string) => {
            return (
              hostname === 'localhost' ||
              hostname === '127.0.0.1' ||
              hostname === '::1' ||
              /^192\.168\./.test(hostname) ||
              /^10\./.test(hostname) ||
              /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
            );
          };
          if (this.webviewDomainWhiteList && this.webviewDomainWhiteList.length > 0) {
            try {
              const { hostname } = new URL(url);
              // 优化白名单判断，支持 .example.com 形式的子域名通配和本地/内网IP
              const isWhiteListed = this.webviewDomainWhiteList.some(domain => {
                if (domain === 'localhost' || domain === '127.0.0.1' || domain === '::1') {
                  return isLocalhost(hostname);
                }
                if (domain.startsWith('.')) {
                  // .example.com 允许所有 *.example.com
                  return hostname === domain.slice(1) || hostname.endsWith(domain);
                } else {
                  // 精确匹配
                  return hostname === domain;
                }
              }) || isLocalhost(hostname); // 允许本地回环和内网地址
              if (isWhiteListed) {
                enable(webContents);
              } else {
                log('log', 'webview 域名未在白名单，未启用 remote', url);
              }
            } catch {
              log('log', 'webview url 解析失败，未启用 remote', url);
            }
          } else {
            enable(webContents); // 没有配置白名单则全部允许
          }
        };
        // 只监听一次，防止多次触发
        const onDidNavigate = () => {
          tryEnable();
          webContents.removeListener('did-navigate', onDidNavigate);
          webContents.removeListener('did-finish-load', onDidNavigate);
        };
        webContents.on('did-navigate', onDidNavigate);
        webContents.on('did-finish-load', onDidNavigate);
      });
      window.webContents.on('close', () => {
        this.windows.delete(window.id || window._id)
      })
      window.webContents.on('destroyed', () => {
        this.windows.delete(window.id || window._id)
      })
      window.webContents.on('dom-ready', () => {
        if (openDevTools) {
          window.webContents.openDevTools();
        }
      });
      if (type === 'BW') {
        // @ts-ignore
        if (options.browserWindow?.show !== false) {
          window.show()
        }
        // @ts-ignore
        window.on('closed', () => {
          log('log', 'closed', window.id, window._name);
          this.windows.delete(window.id || window._id)
        });
      }
      if (type === 'BV') {
        parentWin?.addBrowserView(window);
        log('log', 'create - addBrowserView')
      }

      this.windows.set(window.id || window._id || window.webContents.id, window);
      log('log', 'create', this.windows.keys())

      // 初始化值
      window.webContents.on('did-finish-load', () => {
        console.error('did-finish-load', window.webContents.id);
        initWebContentsVal(window, `${preload || ''}`);
      })
      window.webContents.on('did-start-loading', () => {
        console.error('did-start-loading', window.webContents.id);
        initWebContentsVal(window, `${preload || ''}`);
      })
      if (type === 'BW') {
        const handleBrowserViewFocus = (view: BrowserView) => {
          try {
            view.webContents?.focus();
            view.webContents?.executeJavaScript(`
              try {
                window.dispatchEvent(new Event('focus'));
              } catch (error) {
                console.error('focus', error);
              }
            `)
          } catch (error) {
            log('error', 'handleBrowserViewFocus', error);
          }
        }
        const handleBrowserViewBlur = (view: BrowserView) => {
          try {
            view.webContents?.executeJavaScript(`
              try {
                window.dispatchEvent(new Event('blur'));
              } catch (error) {
                console.error('blur', error);
              }
            `)
          } catch (error) {
            log('error', 'handleBrowserViewBlur', error);
          }
        }
        window.on('focus', () => {
          try {
            if (typeof window.getBrowserViews === 'function') {
              const views = (window as BrowserWindow).getBrowserViews() || [];
              for (const view of views) {
                handleBrowserViewFocus(view)
              }
            }
          } catch (error) {
            log('error', 'focus', error);
          }
        });
        window.on('blur', () => {
          try {
            if (typeof window.getBrowserViews === 'function') {
              const views = (window as BrowserWindow).getBrowserViews();
              for (const view of views) {
                handleBrowserViewBlur(view)
              }
            }
          } catch (error) {
            log('error', 'focus', error);
          }
        });
        try {
          const _addBrowserView = window.addBrowserView;
          window.addBrowserView = (view: BrowserView) => {
            _addBrowserView.call(window, view);
            handleBrowserViewFocus(view)
          }
          const _removeBrowserView = window.removeBrowserView;
          window.removeBrowserView = (view: BrowserView) => {
            _removeBrowserView.call(window, view);
            handleBrowserViewBlur(view)
          }
          const _setBrowserView = window.setBrowserView;
          window.setBrowserView = (view: BrowserView) => {
            const views = window.getBrowserViews() || [];
            for (const view of views) {
              handleBrowserViewBlur(view)
            }
            _setBrowserView.call(window, view);
            handleBrowserViewFocus(view);
          }
        } catch (error) {
          log('error', 'focus', error);
        }
      }

      if (options.url) {
        // @ts-ignore
        window.loadURL ? window.loadURL(options.url) : window.webContents.loadURL(options.url);
        // @ts-ignore
        window.focus ? window.focus() : window.webContents.focus();
      }
    } catch (error) {
      log('error', 'create', error)
    }

    return window;
  }

  _setLoadingView(window: BWItem, createOptions: ElectronWindowsManagerOptions) {
    if (createOptions) {
      const {
        loadingView,
        preventOriginNavigate = false,
      } = createOptions;
      let _loadingView = new BrowserView({
        webPreferences: {
          // session: getCustomSession(),
          contextIsolation: false,
          nodeIntegration: true,
          // 允许loadURL与文件路径在开发环境
          webSecurity: false,
        }
      });

      if (window.isDestroyed()) {
        return;
      }

      const loadLoadingView = () => {
        const [ viewWidth, viewHeight ] = window.getSize();
        window.setBrowserView(_loadingView);
        _loadingView.setBounds({
          x: 0,
          y: 0,
          width: viewWidth || 10,
          height: viewHeight || 10,
        });
        log('log', 'loadLoadingView', window._name);
        _loadingView.webContents.loadURL(loadingView?.url || '')
      };

      const onFailure = lodash.debounce(() => {
        if (_loadingView.webContents && !_loadingView.webContents.isDestroyed()) {
          _loadingView.webContents.close();
        }
        if (window.isDestroyed()) {
          return;
        }

        if (window) {
          window.removeBrowserView(_loadingView);
        }
      }, 300);

      loadLoadingView();

      window.on('resize', lodash.debounce(() => {
        if (_loadingView.webContents && !_loadingView.webContents.isDestroyed()) {
          if (window.isDestroyed()) {
            return;
          }
          const [ viewWidth, viewHeight ] = window.getSize();
          _loadingView.setBounds({ x: 0, y: 0, width: viewWidth || 10, height: viewHeight || 10 });
        }
      }, 500));

      window.webContents.on('will-navigate', (e) => {
        if (preventOriginNavigate) {
          e.preventDefault();
          return;
        }
        if (window.isDestroyed()) {
          return;
        }
        if (_loadingView.webContents && !_loadingView.webContents.isDestroyed()) {
          window.setBrowserView(_loadingView);
        } else {
          // if loadingView has been destroyed
          _loadingView = new BrowserView();
          loadLoadingView();
        }
      });
      window.webContents.on('dom-ready', onFailure);
      window.webContents.on('crashed', onFailure);
      window.webContents.on('unresponsive', onFailure);
      window.webContents.on('did-fail-load', onFailure);
      window.webContents.on('did-finish-load', onFailure);
      window.webContents.on('did-stop-loading', onFailure);
    }
  }

  get(idOrName: string | number): BWItem | BVItem | undefined {
    log('log', 'get', idOrName);
    let win: BWItem | BVItem ;
    this.windows.forEach((i, key) => {
      try {
        if (!(i && i?.webContents?.isDestroyed && !i?.webContents?.isDestroyed?.())) {
          this.windows.delete(key)
        }
      } catch (error) {
        log('error', 'get');
      }

      if (typeof idOrName === 'number') {
        if (i?.id === idOrName || i?._id === idOrName) {
          win = i;
        }
      } else if (typeof idOrName === 'string') {
        if (i?._name === idOrName) {
          win = i;
        }
      }
    })
    // @ts-ignore
    if (win && win?.webContents?.isDestroyed && !win?.webContents?.isDestroyed?.()) {
      return win;
    }
    return undefined;
  }

  getAll(type: 'BW'): Map<number, BWItem>;
  getAll(type: 'BV'): Map<number, BVItem>;
  getAll(): Map<number, BWItem | BVItem>;
  getAll(type?: 'BW' | 'BV'): Map<number, BWItem | BVItem> {
    log('log', 'getAll');
    const bwWindows = new Map<number, BWItem>();
    const bvWindows = new Map<number, BVItem>();
    this.windows.forEach((win, key) => {
      if (!(win && win?.webContents?.isDestroyed && !win?.webContents?.isDestroyed?.())) {
        this.windows.delete(key)
      }
      if (win?._type === 'BW') {
        bwWindows.set(key, win as BWItem);
      }
      if (win?._type === 'BV') {
        bvWindows.set(key, win as BVItem);
      }
    })
    if (type === 'BW') {
      return bwWindows;
    }
    if (type === 'BV') {
      return bvWindows;
    }
    return this.windows;
  }

  close(idOrName: string | number) {
    log('log', 'close', idOrName);
    let win: BWItem | BVItem | undefined = undefined;
    this.windows.forEach((i) => {
      if (typeof idOrName === 'number') {
        if (i?.id === idOrName || i?._id === idOrName) {
          win = i;
        }
      } else if (typeof idOrName === 'string') {
        if (i?._name === idOrName) {
          win = i;
        }
      }
    })
    // @ts-ignore
    win && this.windows.delete(win?.id)
    // @ts-ignore
    if (win) {
      // @ts-ignore
      if (win._type === 'BV') {
        this.windows.forEach(i => {
          if (i?._type === 'BW') {
            const _win = i as BWItem;
            _win.removeBrowserView(win as BVItem);
          }
        })
      }
      // @ts-ignore
      (win as BVItem | BWItem)?.webContents?.destroy?.();
      // @ts-ignore
      win?.close?.();
      // @ts-ignore
      win?.destroy?.();
    }
    return true;
  }

  closeAll() {
    log('log', 'closeAll');
    this.windows.forEach((win) => {
      try {
        win && this.windows.delete(win?.id)
        // @ts-ignore
        if (win._type === 'BV') {
          this.windows.forEach(i => {
            if (i?._type === 'BW') {
              const _win = i as BWItem;
              _win.removeBrowserView(win as BVItem);
            }
          })
        }
        // @ts-ignore
        (win as BVItem | BWItem)?.webContents?.destroy?.();
        // @ts-ignore
        win?.close?.();
        // @ts-ignore
        win?.destroy?.();
      } catch (error) {

      }
    })
  }

  rename(idOrName: string | number, newName: string) {
    log('log', 'rename', idOrName, newName);
    let win: BWItem | BVItem | undefined = undefined;

    // 先查找目标窗口
    if (typeof idOrName === 'number') {
      win = this.get(idOrName);
    } else if (typeof idOrName === 'string') {
      this.windows.forEach(i => {
        if (i?._name === idOrName) {
          win = i;
        }
      });
    }
    if (!win) {
      // 没有找到目标窗口
      return undefined;
    }

    // 检查新名字是否已存在
    let nameExists = false;
    this.windows.forEach(i => {
      if (i !== win && i?._name === newName) {
        nameExists = true;
      }
    });
    if (nameExists) {
      // 新名字已被占用
      return undefined;
    }

    // 修改名字并同步 webContents
    win._name = newName;
    initWebContentsVal(win, `${this.preload || ''}`);
    return win;
  }

  reInitUrl(idOrName: string | number, url: string): BWItem | BVItem | undefined {
    log('log', 'reInitUrl', idOrName, url);
    let win: BWItem | BVItem | undefined = undefined;
    this.windows.forEach(i => {
      if (i?._name === idOrName) {
        win = i;
      }
    });
    if (!win) {
      return undefined;
    }
    (win as BWItem | BVItem)._initUrl = url;
    initWebContentsVal(win, `${this.preload || ''}`);
    return win;
  }

  getPreload() {
    log('log', 'getPreload');
    return this.preload;
  }

  private _applyBrowserWindowOptions(win: BWItem, options: ElectronWindowsManagerOptions) {
    const browserWindowOptions = options.browserWindow as Partial<BrowserWindowConstructorOptions> || {};
    // 设置父窗口
    if (typeof browserWindowOptions.parent === 'number') {
      const parentWin = BrowserWindow.fromId(browserWindowOptions.parent);
      if (parentWin) {
        win.setParentWindow(parentWin);
      }
    }
    // 设置窗口大小
    if (typeof browserWindowOptions.width === 'number' && typeof browserWindowOptions.height === 'number') {
      win.setBounds({ width: browserWindowOptions.width, height: browserWindowOptions.height });
    }
    // 置顶
    if (typeof browserWindowOptions.alwaysOnTop === 'boolean') {
      win.setAlwaysOnTop(browserWindowOptions.alwaysOnTop);
    }
    // 设置背景颜色
    if (typeof browserWindowOptions.backgroundColor === 'string') {
      win.setBackgroundColor(browserWindowOptions.backgroundColor);
    }
    // 居中
    if (browserWindowOptions?.center !== false) {
      win.center();
    }
    // 设置窗口移动
    if (typeof browserWindowOptions.movable === 'boolean') {
      win.setMovable(browserWindowOptions.movable);
    }
    // 设置窗口大小调整
    if (typeof browserWindowOptions.resizable === 'boolean') {
      win.setResizable(browserWindowOptions.resizable);
    }
    // 设置全屏模式
    if (typeof browserWindowOptions.fullscreenable === 'boolean') {
      win.setFullScreenable(browserWindowOptions.fullscreenable);
    }
    // 设置窗口阴影
    if (typeof browserWindowOptions.hasShadow === 'boolean') {
      win.setHasShadow(browserWindowOptions.hasShadow);
    }
    // 设置窗口最小尺寸
    if (typeof browserWindowOptions.minWidth === 'number' && typeof browserWindowOptions.minHeight === 'number') {
      win.setMinimumSize(browserWindowOptions.minWidth, browserWindowOptions.minHeight);
    }
    // 设置窗口最大尺寸
    if (typeof browserWindowOptions.maxWidth === 'number' && typeof browserWindowOptions.maxHeight === 'number') {
      win.setMaximumSize(browserWindowOptions.maxWidth, browserWindowOptions.maxHeight);
    }
    // 设置窗口位置
    if (typeof browserWindowOptions.x === 'number' && typeof browserWindowOptions.y === 'number') {
      win.setPosition(browserWindowOptions.x, browserWindowOptions.y);
    }
    // 设置窗口标题
    if (typeof browserWindowOptions.title === 'string') {
      win.setTitle(browserWindowOptions.title);
    }
    // 设置窗口图标
    if (typeof browserWindowOptions.icon === 'string') {
      win.setIcon(browserWindowOptions.icon);
    }
    // 设置窗口菜单栏可见性
    if (typeof browserWindowOptions.autoHideMenuBar === 'boolean') {
      win.setAutoHideMenuBar(browserWindowOptions.autoHideMenuBar);
    }
    // 设置窗口最小化按钮
    if (browserWindowOptions.minimizable === false) {
      win.setMinimizable(false);
    }
    // 设置窗口最大化按钮
    if (browserWindowOptions.maximizable === false) {
      win.setMaximizable(false);
    }
    // 设置窗口关闭按钮
    if (browserWindowOptions.closable === false) {
      win.setClosable(false);
    }
    // 设置窗口焦点
    if (browserWindowOptions.focusable === false) {
      win.setFocusable(false);
    }
    // 设置窗口全屏
    if (browserWindowOptions.fullscreen === true) {
      win.setFullScreen(true);
    }
    // 设置窗口背景材质
    if (typeof browserWindowOptions.vibrancy === 'string') {
      win.setVibrancy(browserWindowOptions.vibrancy);
    }
    // 设置窗口透明度
    if (typeof browserWindowOptions.opacity === 'number') {
      win.setOpacity(browserWindowOptions.opacity);
    }
    // 设置窗口显示状态
    if (browserWindowOptions.show === false) {
      win.hide();
    }

    // 可继续扩展其他动态属性
  }

  private _applyBrowserViewOptions(view: BVItem, options: ElectronWindowsManagerOptions) {
    const browserWindowOptions = options.browserWindow as Partial<BrowserWindowConstructorOptions> || {};
    let parentWin: BrowserWindow | undefined;
    if (typeof browserWindowOptions.parent === 'number') {
      parentWin = BrowserWindow.fromId(browserWindowOptions.parent) || undefined;
      if (parentWin) {
        parentWin.addBrowserView(view);
      }
    }
    // 设置视图大小
    if (typeof browserWindowOptions.width === 'number' && typeof browserWindowOptions.height === 'number') {
      view.setBounds({ x: 0, y: 0, width: browserWindowOptions.width, height: browserWindowOptions.height });
    }
    // 设置视图位置
    if (typeof browserWindowOptions.x === 'number' && typeof browserWindowOptions.y === 'number') {
      const bounds = view.getBounds();
      view.setBounds({
        x: browserWindowOptions.x,
        y: browserWindowOptions.y,
        width: bounds.width,
        height: bounds.height
      });
    }
    // 设置视图背景颜色
    if (typeof browserWindowOptions.backgroundColor === 'string') {
      view.setBackgroundColor(browserWindowOptions.backgroundColor);
    }
    // 可继续扩展其他动态属性
  }

  // 生成一个bv 做为预加载资源窗口，加载完成后销毁
  async createPreloadWebContents(url: string) {
    return new Promise(async (resolve, reject) => {
      let bv: any = await this.create({
        type: 'BV',
        url,
        name: `preload-web-contents-${md5(url)}`,
        extraData: `${url}`
      })
      bv.webContents.on('did-finish-load', () => {
        this.close(bv.id || bv._id)
        resolve(true);
        bv = null;
      })
      bv.webContents.on('did-fail-load', () => {
        this.close(bv.id || bv._id)
        reject(false);
        bv = null;
      })
      bv.webContents.loadURL(url);
    })
  }
}
// @ts-ignore
global['__ELECTRON_WINDOWS_MANAGER__'] = undefined;

export let isInitialized = false;

export const initialize = (preload?: string, loadingViewUrl?: string, errorViewUrl?: string, preloadWebContentsConfig?: PreloadWebContentsConfig, webviewDomainWhiteList?: string[]): WindowsManager => {
  // @ts-ignore
  if (isInitialized && global['__ELECTRON_WINDOWS_MANAGER__']) {
    // @ts-ignore
    return global['__ELECTRON_WINDOWS_MANAGER__'];
  }
  isInitialized = true;
  // @ts-ignore
  const wm = global['__ELECTRON_WINDOWS_MANAGER__'] = new WindowsManager(preload, loadingViewUrl, errorViewUrl, preloadWebContentsConfig, webviewDomainWhiteList)
  eIpc.mainIPC.handleRenderer('__ELECTRON_WINDOW_MANAGER_IPC_CHANNEL__', async (data: any) => {
    if (data?.type === 'create') {
      const opt = data as {
        type: 'create';
        data: ElectronWindowsManagerOptions
      };
      const findWin = wm.get(opt.data.name);
      if (findWin) {
        // @ts-ignore
        findWin?.focus?.();
        findWin?.webContents.focus?.();
        if (opt.data.url && findWin.webContents.getURL() !== opt.data.url) {
          findWin.webContents.loadURL(opt.data.url)
          /** fix: url hash 无法正常加载  */
          setTimeout(()=> {
            findWin.webContents.reload();
          }, 100);
        }
        return {
          winId: Number(`${findWin?.id || findWin?._id || - 1}`),
          winName: `${findWin?._name || ''}`,
          winType: `${findWin?._type || ''}`,
          winExtraData: `${findWin?._extraData || ''}`,
          winInitUrl: `${findWin?._initUrl || ''}`,
        };
      }
      const res = await wm.create(opt.data)
      return {
        winId: Number(`${res.id || res._id || -1}`),
        winName: `${res?._name || ''}`,
        winType: `${res?._type || ''}`,
        winExtraData: `${res?._extraData || ''}`,
        winInitUrl: `${res?._initUrl || ''}`,
      };
    }
    if (data?.type === 'get') {
      const opt = data as {
        type: 'get';
        data: string | number;
      };
      const res = wm.get(opt?.data)
      return  {
        winId: Number(`${res?.id || res?._id || -1}`),
        winName: `${res?._name || ''}`,
        winType: `${res?._type || ''}`,
        winExtraData: `${res?._extraData || ''}`,
        winInitUrl: `${res?._initUrl || ''}`,
      };
    }

    if (data?.type === 'getAll') {
      const opt = data as {
        type: 'getAll';
        data: any;
      };
      const res = wm.getAll()
      const obj = {}
      res.forEach(i => {
        // @ts-ignore
        obj[i.id || i._id] = {
          winId: Number(`${i?.id || i?._id || -1}`),
          winName:  `${i?._name || ''}`,
          winType: `${i?._type || ''}`,
          winExtraData: `${i?._extraData || ''}`,
          winInitUrl: `${i?._initUrl || ''}`,
        }
      })
      return obj;
    }

    if (data?.type === 'close') {
      const opt = data as {
        type: 'close';
        data: string | number;
      };
      const res = wm.close(opt?.data)
      return  res;
    }

    if (data?.type === 'closeAll') {
      const opt = data as {
        type: 'closeAll';
        data: undefined;
      };
      const res = wm.closeAll()
      return res;
    }

    if (data?.type === 'rename') {
      const opt = data as {
        type: 'rename';
        data: {
          idOrName: string | number;
          newName: string;
        };
      };
      const res = wm.rename(opt.data.idOrName, opt.data.newName)
      if (res) {
        return {
          winId: Number(`${res?.id || res?._id || -1}`),
          winName: `${res?._name || ''}`,
          winType: `${res?._type || ''}`,
          winExtraData: `${res?._extraData || ''}`,
          winInitUrl: `${res?._initUrl || ''}`,
        };
      }
      return undefined;
    }

    if (data?.type === 'reInitUrl') {
      const opt = data as {
        type: 'reInitUrl';
        data: {
          idOrName: string | number;
          url: string;
        };
      };
      const res = wm.reInitUrl(opt.data.idOrName, opt.data.url)
      if (res) {
        return {
          winId: Number(`${res?.id || res?._id || -1}`),
          winName: `${res?._name || ''}`,
          winType: `${res?._type || ''}`,
          winExtraData: `${res?._extraData || ''}`,
          winInitUrl: `${res?._initUrl || ''}`,
        };
      }
      return undefined;
    }

    if (data?.type === 'getWindowForWebContentId') {
      const opt = data as {
        type: 'getWindowForWebContentId';
        data: number;
      };
      const targetWebContents = webContents.fromId(opt.data);
      if (targetWebContents) {
        let win = BrowserWindow.fromWebContents(targetWebContents)
        if (!win) {
          // 获取所有的 BrowserWindows
          let allWindows = BrowserWindow.getAllWindows();

          // 遍历所有窗口，检查每个窗口的 BrowserView
          for (let _win of allWindows) {
            let views = _win.getBrowserViews();

            // 遍历窗口的所有 BrowserView
            for (let view of views) {
              if (view.webContents === targetWebContents) {
                win = _win;
                break;
              }
            }

            if (win) break;
          }
        }
        // @ts-ignore
        return win?.id || win?._id;
      }
      return undefined;
    }

    if (data?.type === 'getPreload') {
      const opt = data as {
        type: 'getPreload';
        data: number;
      };
      const res = wm.getPreload()
      return res;
    }

    if (data?.type === 'borrowView_getBounds') {
      const opt = data as {
        type: 'borrowView_getBounds';
        data: {
          webContentId: number;
          options: {};
        };
      };
      const bv = wm.get(opt.data.webContentId)
      if (bv) {
        return bv.getBounds()
      }
      return undefined
    }
    if (data?.type === 'borrowView_setBounds') {
      const opt = data as {
        type: 'borrowView_setBounds';
        data: {
          webContentId: number;
          options: {
            x: number;
            y: number;
            width: number;
            height: number;
          };
        };
      };
      const bv = wm.get(opt.data.webContentId)
      if (bv) {
        bv.setBounds(opt.data.options)
      }
      return undefined
    }
    if (data?.type === 'borrowView_setAutoResize') {
      const opt = data as {
        type: 'borrowView_setAutoResize';
        data: {
          webContentId: number;
          options: {
            width: boolean;
            height: boolean;
            horizontal: boolean;
            vertical: boolean;
          };
        };
      };
      const bv = wm.get(opt.data.webContentId) as BVItem;
      if (bv) {
        bv.setAutoResize(opt.data.options)
      }
      return undefined
    }
    if (data?.type === 'borrowView_setBackgroundColor') {
      const opt = data as {
        type: 'borrowView_setBackgroundColor';
        data: {
          webContentId: number;
          options: string;
        };
      };
      const bv = wm.get(opt.data.webContentId) as BVItem;
      if (bv) {
        bv.setBackgroundColor(opt.data.options)
      }
      return undefined
    }
    // 是否开启预加载窗口
    if (data?.type === 'setPreloadWebContentsConfig') {
      const opt = data as {
        type: 'setPreloadWebContentsConfig';
        data: PreloadWebContentsConfig;
      };
      wm.setPreloadWebContentsConfig(opt.data)
    }

    if (data?.type === 'createPreloadWebContents') {
      const opt = data as {
        type: 'createPreloadWebContents';
        data: string;
      };
      const res = await wm.createPreloadWebContents(opt.data)
      return res;
    }

    return undefined
  })
  return wm;
}

const log = (type: 'log' | 'error', ...data: any[]) => {
  const key = `[electron-window-manager]: `
  try {
    console[type](key, ...data);
  } catch (error) {
    console.error(key, error)
  }
}
